import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getOwnedCallLead } from "../../lib/workspaceCallLeadApi";
import { toCustomerTokens, toLeadProjection } from "./workspaceCustomerAdapters";

/**
 * AW-2C-008 -- the owned call's customer, fetched early and published
 * only against proof.
 *
 * (The real runner component is named only in `WorkspaceScriptRunner`,
 * the one file in this feature permitted to -- the global-shell guard
 * enforces that on raw source, comments included. So this prose says
 * "the Script runner" throughout, for the same reason
 * `useWorkspaceToolData` does.)
 *
 * ONE RESOURCE, TWO CONSUMERS
 * ---------------------------
 * The Lead Details panel and the Script runner's customer merge fields
 * need the same information about the same call. Fetching it twice
 * would be two provider reads for one answer and -- worse -- two
 * answers that could disagree after a release-and-reclaim. So this hook
 * is mounted ONCE, in the Production shell above both consumers, and
 * they receive its output as props. Switching tools cannot re-enter it
 * and a runner remount cannot either, because neither owns it.
 *
 * WHAT CHANGED IN AW-2C-008
 * -------------------------
 * AW-2C-005 fetched after the run id arrived, which put Script Run
 * creation -- six Mongo operations and a provider round trip of its own
 * -- on the critical path for the first thing an agent says. The fetch
 * now starts the moment the workspace sees a verified Connected,
 * concurrently with that work.
 *
 * The response is NOT published when it arrives. It is held until the
 * run's own response is available, and published only if the two
 * server-issued correlation identifiers are equal.
 *
 * PROOF, NOT TIMING
 * -----------------
 * This is the part worth reading carefully, because the obvious
 * argument is wrong.
 *
 * It is tempting to say: both requests were issued microseconds apart
 * from the same transition, so they must describe the same call. That
 * is a claim about interleavings, not a fact about the data -- and
 * AW-2C-007 found that nothing in either response could establish it,
 * which is why that slice stopped rather than shipping the reasoning.
 *
 * What makes it provable is `call_correlation_id`: an opaque, keyed
 * derivation over the owned call's own authoritative binding material,
 * issued independently by both routes from their own reads of the same
 * durable obligation. Equality means the two responses describe the
 * same verified owned call. Inequality means they do not, whatever the
 * clock said.
 *
 * Three things are therefore NEVER used to decide publication:
 *
 *   * request timing or effect ordering;
 *   * `lead_id` equality -- a redial of the same lead is a different
 *     call, and the correlation changes for it precisely because it
 *     derives from the callerid rather than the lead;
 *   * any counter this module keeps. `attemptRef` below stamps
 *     in-flight work so a superseded response can be dropped. It is
 *     never compared against anything the server issued and can never
 *     admit a response -- only discard one.
 *
 * WHAT HAPPENS WHEN THEY DISAGREE
 * -------------------------------
 * The held response is discarded and a new fetch is issued. A mismatch
 * means "this answer is about a call that is no longer the one in
 * front of the agent", and the honest response to that is to ask
 * again, not to show it and not to give up.
 *
 * DEGRADES TO THE OLD BEHAVIOUR, DELIBERATELY
 * -------------------------------------------
 * When no correlation secret is configured both routes return `null`
 * for it. Two nulls are not a match -- a missing proof is not a proof
 * -- so nothing is published early. The hook then publishes on the
 * run-id path exactly as AW-2C-005 did: slower, and equally correct.
 * There is no code path in which an unproven response is shown.
 *
 * RETAINED WRAP-UP, AND OWNERSHIP LOSS
 * ------------------------------------
 * `eligible` spans Connected, Hanging-up and Wrap-up, so nothing clears
 * while an agent chooses a disposition -- and the correlation is stable
 * across that phase change because the obligation keeps the same
 * callerid until disposition. When eligibility ends, everything clears.
 *
 * NO PERSISTENCE, NO POLLING
 * --------------------------
 * Ephemeral React state only. No browser-storage API of any kind, no
 * module-level cache: a customer's name, telephone number and address
 * live in this component's state for the duration of one call and
 * nowhere else, and neither does the correlation identifier.
 *
 * (The storage APIs are not named here on purpose -- the global-shell
 * guard scans this feature's raw source, comments included, and a
 * docstring that spells out the token it forbids trips the guard on its
 * own explanation. The suite asserts the real absence both statically
 * and after a render.)
 *
 * One request per owned call, plus one more only when a correlation
 * mismatch proves the first was about a different call. No interval, no
 * retry loop, no refetch on re-render. The workspace already polls call
 * state and must not gain a second cadence.
 */

/**
 * A monotonic millisecond reading, or `null` where the browser has no
 * high-resolution clock.
 *
 * `performance.now()` rather than `Date.now()`: these measure elapsed
 * intervals, and a wall clock can step backwards (NTP, a user changing
 * the time) and produce a negative "latency".
 */
function now() {
  if (typeof performance === "object" && typeof performance.now === "function") {
    return performance.now();
  }
  return null;
}

function elapsed(from, to) {
  if (from === null || from === undefined || to === null || to === undefined) return null;
  return Math.max(0, Math.round(to - from));
}

/**
 * The four segments AW-2C-008 asks to be instrumented separately.
 *
 * Privacy-safe by construction: every field is a number or `null`. No
 * customer value, no native lead id, no callerid, no lease id, no
 * correlation identifier -- the honest way to honour "do not log
 * sensitive call identifiers" is to measure something that cannot carry
 * one.
 *
 * Returned rather than logged. The browser has no durable log sink in
 * this system; the server records its own authorize/provider/revalidate
 * segments, and these are the client-side four.
 */
const NO_TIMINGS = Object.freeze({
  connectedToRequestMs: null,
  requestToResponseMs: null,
  responseToCorrelationMs: null,
  correlationToRenderMs: null,
});

const IDLE = Object.freeze({
  loading: false,
  customer: null,
  tokens: null,
  commentsTruncated: false,
  unavailable: false,
  elapsedMs: null,
  timings: NO_TIMINGS,
});

const LOADING = Object.freeze({ ...IDLE, loading: true });

/**
 * Every refusal collapses to one consumer-visible fact: unavailable.
 *
 * The route distinguishes five outcomes and logs them server-side,
 * where they are actionable. An agent mid-call cannot act on any of
 * them differently -- "no eligible owned call", "lead not found" and
 * "the provider is down" all mean *you do not have a name on screen,
 * carry on* -- and a Script that rendered a different apology per
 * backend state would be worse to read than one that rendered none.
 *
 * Deliberately NOT an error: nothing here throws, and `unavailable`
 * must never block the Script runner.
 */
const UNAVAILABLE_BASE = Object.freeze({ ...IDLE, unavailable: true });

/**
 * The internal record. Two of its fields are private and are stripped
 * before anything reaches a consumer:
 *
 *   `held`         -- a response in hand that has NOT been proven to
 *                     belong to the call on screen. Invisible by
 *                     construction: the returned projection is built
 *                     from named fields, so a held customer cannot
 *                     reach a panel even by accident.
 *   `publishedFor` -- the correlation the published customer belongs
 *                     to, so a match is not republished every render.
 *
 * They live in state rather than refs because the clear has to happen
 * DURING RENDER -- an effect is a frame too late, and that frame is a
 * name an agent may read aloud -- and refs must not be written during
 * render (`react-hooks/refs`, and it is right).
 */
const BLANK = Object.freeze({ ...IDLE, held: null, publishedFor: null, mismatches: 0 });

/**
 * How many times a mismatch may be retried before the customer is
 * reported unavailable.
 *
 * One. A mismatch means the answer in hand is about a different owned
 * call, and asking once more is the right response -- the usual cause
 * is a call that moved while the read was in flight, and the second
 * read is issued against the new one.
 *
 * A SECOND mismatch means the two sides are not converging, and the
 * honest answer is then "no customer on screen" rather than a third
 * provider read. Without this bound a persistent disagreement would
 * refetch forever: hold, mismatch, refetch, hold, mismatch -- an
 * unbounded loop against the provider, triggered by exactly the
 * condition that already indicates something is wrong.
 */
const MISMATCH_RETRY_LIMIT = 1;

export default function useOwnedCallCustomer({ eligible, runId, correlationId }) {
  const [record, setRecord] = useState(BLANK);

  /**
   * Whether a request is in flight, and a stamp for superseded work.
   *
   * `attemptRef` is NOT an identity. It is never compared against
   * anything the server issued, and it can only ever cause a response
   * to be discarded -- never admitted. Publication is decided by the
   * correlation and by nothing else.
   *
   * Both are touched only inside effects and the async callback, never
   * during render.
   */
  const inFlightRef = useRef(false);
  const attemptRef = useRef(0);

  /** When eligibility began, for segment one. Effects only. */
  const connectedAtRef = useRef(null);

  const isEligible = Boolean(eligible);
  const currentRunId = runId ?? null;

  /**
   * What counts as a NEW episode -- and, just as importantly, what does
   * not.
   *
   * Eligibility changing is a new episode: a call began or ended.
   *
   * The run id ARRIVING is not. It goes `null` -> value partway through
   * every episode, because the fetch now starts before Script Run
   * creation finishes; treating that as a new call would discard the
   * held response at precisely the moment its proof turns up, which
   * defeats the entire optimization. (It did, until this comparison was
   * written correctly -- `LATE run/start: the held response publishes
   * when the proof lands` is the test that catches it.)
   *
   * The run id MOVING between two non-null values is a new episode: one
   * owned call's run replaced by another's.
   */
  const [episode, setEpisode] = useState({ eligible: false, runId: null });
  const eligibilityChanged = episode.eligible !== isEligible;
  const runReplaced = episode.runId !== null && episode.runId !== currentRunId;

  if (eligibilityChanged || runReplaced) {
    setEpisode({ eligible: isEligible, runId: currentRunId });
    // Nothing from the previous call may survive into this render,
    // published or held.
    setRecord(isEligible ? { ...BLANK, loading: true } : BLANK);
  } else if (episode.runId !== currentRunId) {
    // The run id arrived (or was cleared) within this episode. Record
    // it so a later replacement is detectable, and keep everything
    // else -- including a held response waiting for exactly this.
    setEpisode({ eligible: isEligible, runId: currentRunId });
  }

  const fetchCustomer = useCallback(async () => {
    const attempt = attemptRef.current;
    inFlightRef.current = true;
    const requestedAt = now();

    let data = null;
    let failed = false;
    try {
      data = await getOwnedCallLead();
    } catch (error) {
      failed = true;
    }

    if (attemptRef.current !== attempt) return;
    inFlightRef.current = false;
    const respondedAt = now();
    const partial = {
      ...NO_TIMINGS,
      connectedToRequestMs: elapsed(connectedAtRef.current, requestedAt),
      requestToResponseMs: elapsed(requestedAt, respondedAt),
    };

    const customer = failed ? null : toLeadProjection(data);
    if (customer === null) {
      // Either the read was refused, or it was a 200 carrying no lead
      // identity -- which is not a customer with no details but a
      // response this surface cannot speak for. Reported immediately:
      // there is nothing to correlate, and an agent waiting on a name
      // they will never get is worse than being told.
      setRecord({ ...BLANK, ...UNAVAILABLE_BASE, timings: partial });
      return;
    }

    // HELD, not published. Consumers still see `loading`.
    setRecord((current) => ({
      ...current,
      loading: true,
      held: {
        correlationId: data?.call_correlation_id ?? null,
        customer,
        tokens: toCustomerTokens(data),
        commentsTruncated: Boolean(data?.comments_truncated),
        requestedAt,
        respondedAt,
      },
    }));
  }, []);

  const publish = useCallback((held) => {
    const correlatedAt = now();
    setRecord({
      loading: false,
      customer: held.customer,
      tokens: held.tokens,
      commentsTruncated: held.commentsTruncated,
      unavailable: false,
      elapsedMs: elapsed(held.requestedAt, correlatedAt),
      timings: {
        connectedToRequestMs: elapsed(connectedAtRef.current, held.requestedAt),
        requestToResponseMs: elapsed(held.requestedAt, held.respondedAt),
        responseToCorrelationMs: elapsed(held.respondedAt, correlatedAt),
        // Publication and render happen in the same commit, so this is
        // measured as that commit rather than guessed. Zero is an
        // honest answer here, not a missing one.
        correlationToRenderMs: 0,
      },
      held: null,
      publishedFor: held.correlationId,
      mismatches: 0,
    });
  }, []);

  /**
   * One effect: issue, hold, and publish only against proof.
   *
   * Both state writes happen after an `await`, which is not decoration.
   * `react-hooks/set-state-in-effect` is an error in the standalone
   * plugin this repository lints with (7.0.1) and is UNDEFINED in the
   * copy the BUILD uses (react-scripts' 4.6.2) -- so silencing it with
   * a directive fails compilation outright, which is how `main` was
   * broken once already.
   *
   * The accommodation is a microtask hop, not a timer: no frame is
   * skipped and no delay is introduced on the path this slice exists to
   * make fast. It is the same shape the merged fetch already uses, and
   * it is stated rather than hidden because a reader is entitled to
   * know an `await Promise.resolve()` is there for a linter.
   */
  useEffect(() => {
    if (!isEligible) {
      connectedAtRef.current = null;
      inFlightRef.current = false;
      attemptRef.current += 1;
      return;
    }
    if (connectedAtRef.current === null) connectedAtRef.current = now();

    const held = record.held;
    // Two nulls are not a match: a missing proof is not a proof, so an
    // unissued correlation never admits a response.
    const canCorrelate = Boolean(currentRunId && correlationId);

    if (held !== null && canCorrelate) {
      void (async () => {
        await Promise.resolve();
        if (held.correlationId === correlationId) {
          publish(held);
          return;
        }
        // Proven to be about a different owned call. Never published.
        setRecord((current) => {
          if (current.held === null) return current;
          const mismatches = current.mismatches + 1;
          if (mismatches > MISMATCH_RETRY_LIMIT) {
            // Not converging. Withhold rather than keep asking.
            return { ...current, ...UNAVAILABLE_BASE, held: null, mismatches };
          }
          return { ...current, held: null, loading: true, mismatches };
        });
      })();
      return;
    }

    // Held, and the proof has not arrived yet. Wait -- this is the
    // ordinary case, and the entire reason the response is held.
    if (held !== null) return;

    if (inFlightRef.current) return;
    // Already published for this call. Without this the publish above
    // would clear `held`, this effect would re-run, and it would fetch
    // again forever.
    if (record.publishedFor !== null) return;
    if (record.unavailable) return;

    void (async () => {
      await fetchCustomer();
    })();
  }, [
    isEligible,
    currentRunId,
    correlationId,
    record.held,
    record.publishedFor,
    record.unavailable,
    fetchCustomer,
    publish,
  ]);

  /**
   * The consumer-visible projection, built from NAMED fields.
   *
   * `held` and `publishedFor` are not listed, so an unproven customer
   * cannot reach the Lead panel or a Script token even through a
   * careless spread. Memoized so the object identity changes only when
   * something a consumer reads changes -- the Script runner lists this
   * as a dependency of its token renderer, and a fresh object every
   * render would rebuild that callback for no reason.
   */
  return useMemo(
    () => ({
      loading: record.loading,
      customer: record.customer,
      tokens: record.tokens,
      commentsTruncated: record.commentsTruncated,
      unavailable: record.unavailable,
      elapsedMs: record.elapsedMs,
      timings: record.timings,
    }),
    [
      record.loading,
      record.customer,
      record.tokens,
      record.commentsTruncated,
      record.unavailable,
      record.elapsedMs,
      record.timings,
    ]
  );
}
